import { useEffect, useRef, useState } from "react";
import _get from "lodash/get";

import { useAppSelector } from "../../../../../redux/hooks";
import { selectSyncEnabledRetailers } from "../../../../../redux/selectors";
import { RetailerWithSyncStatus } from "../../../../../redux/types";
import { useSyncContext } from "../../../../SyncProvider";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { getLatestSyncSession } from "../../../../../api";
import { RetailerConnections } from "../../../../../types/misc";

const sliceRetailer = (retailers: RetailerWithSyncStatus[], size?: number) => {
  // Create a copy before sorting to avoid mutating the original array
  const sortedRetailers = [...(retailers || [])].sort((a, b) => {
    return (
      (b?.retailerConnectRewardAmount ?? 0) -
      (a?.retailerConnectRewardAmount ?? 0)
    );
  });
  if (!size) return sortedRetailers;
  return sortedRetailers.slice(0, size);
};

export const useFetchAccount = ({ pageSize }: { pageSize?: number }) => {
  const [loading, setLoading] = useState(true);
  const [connectedRetailers, setConnectedRetailers] = useState<
    RetailerWithSyncStatus[]
  >([]);
  const [disconnectedRetailers, setDisconnectedRetailers] = useState<
    RetailerWithSyncStatus[]
  >([]);
  const [newRetailers, setNewRetailers] = useState<RetailerWithSyncStatus[]>(
    []
  );
  const [localRetailerConnections, setLocalRetailerConnections] =
    useState<RetailerConnections | null>(null);
  const [localLoading, setLocalLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  const { signedIn } = useSignedIn();

  const retailers = useAppSelector(selectSyncEnabledRetailers);
  const appConfigId = useAppSelector(
    (state) => state.appConfig?.brandAppConfig?.appConfigId
  );
  const {
    loading: syncLoading,
    syncStatus,
    syncStatusByParentRetailer,
    retailerConnections: contextRetailerConnections,
  } = useSyncContext();

  // Check if sync is enabled (only for brandclub stores)
  const isSyncEnabled = Boolean(
    appConfigId?.toLowerCase().includes("brandclub")
  );

  // Use context retailerConnections if sync is enabled, otherwise use locally fetched ones
  const retailerConnections = isSyncEnabled
    ? contextRetailerConnections
    : localRetailerConnections;

  const isDataReady = isSyncEnabled ? !syncLoading : !localLoading;

  // Fetch retailer connections directly if sync is not enabled
  useEffect(() => {
    // Wait for appConfigId to be loaded before deciding
    if (appConfigId === undefined) {
      return;
    }

    if (isSyncEnabled) {
      setLocalLoading(false);
      return;
    }

    if (hasFetchedRef.current) {
      return;
    }

    if (!signedIn) {
      // Not signed in, set empty connections
      setLocalRetailerConnections({});
      setLocalLoading(false);
      return;
    }

    const fetchRetailerConnections = async () => {
      hasFetchedRef.current = true;
      try {
        const getLatestSyncSessionResponse = await getLatestSyncSession();
        const retailerConnectionsFromApi = _get(
          getLatestSyncSessionResponse,
          ["data", "retailerConnections"],
          {}
        );
        setLocalRetailerConnections(retailerConnectionsFromApi);
      } catch (error) {
        console.error("Failed to fetch retailer connections:", error);
        setLocalRetailerConnections({});
      } finally {
        setLocalLoading(false);
      }
    };

    fetchRetailerConnections();
  }, [appConfigId, isSyncEnabled, signedIn]);

  // Process retailers when data is ready
  useEffect(() => {
    // Wait for appConfigId to be loaded
    if (appConfigId === undefined) {
      return;
    }

    if (!isDataReady) {
      return;
    }

    const currentConnections = retailerConnections || {};

    const filterRetailersByConnectionStatus = (
      serverConnectionStatus: string[],
      displayConnectionStatus: "valid" | "invalid" | "new"
    ): RetailerWithSyncStatus[] => {
      return (
        retailers
          .filter((retailer) => {
            return signedIn
              ? serverConnectionStatus.includes(
                  currentConnections[
                    `${retailer.parentRetailerId || retailer.retailerId}`
                  ]?.connectionStatus
                )
              : displayConnectionStatus === "new";
          })
          .map((r) => {
            return {
              ...r,
              connectionStatus: displayConnectionStatus,
              currentSyncStatus:
                syncStatus === "normal"
                  ? "none"
                  : syncStatusByParentRetailer.find(
                      (x) => x.parentRetailerId === r.parentRetailerId
                    )
                  ? "syncing"
                  : "anotherSyncInProgress",
            };
          }) || []
      );
    };

    const connectedRetailersUpdated: RetailerWithSyncStatus[] =
      filterRetailersByConnectionStatus(["valid"], "valid");
    const disconnectedRetailersUpdated: RetailerWithSyncStatus[] =
      filterRetailersByConnectionStatus(["invalid", "retry"], "invalid");
    const newRetailersUpdated: RetailerWithSyncStatus[] =
      filterRetailersByConnectionStatus(["notConnected"], "new");

    setConnectedRetailers(
      sliceRetailer(connectedRetailersUpdated || [], pageSize)
    );
    setDisconnectedRetailers(
      sliceRetailer(disconnectedRetailersUpdated || [], pageSize)
    );
    setNewRetailers(sliceRetailer(newRetailersUpdated || [], pageSize));
    setLoading(false);
  }, [
    appConfigId,
    isDataReady,
    retailers,
    retailerConnections,
    signedIn,
    syncStatus,
    syncStatusByParentRetailer,
    pageSize,
  ]);

  return {
    connectedRetailers,
    disconnectedRetailers,
    newRetailers,
    loading,
  };
};
